import { entryChannels, accessSteps } from "@/mocks/addresses";
import type { Notice } from "@/pages/home/types";
import SectionHeading from "@/pages/home/components/SectionHeading";

interface EntrySectionProps {
  onOpenNotice: (notice: Notice) => void;
}

const accentMap: Record<string, { icon: string; button: string; ring: string }> = {
  primary: {
    icon: "bg-primary-500 text-background-50",
    button: "bg-primary-500 text-background-50 hover:bg-primary-600",
    ring: "border-primary-200",
  },
  accent: {
    icon: "bg-accent-500 text-foreground-950",
    button: "bg-accent-500 text-foreground-950 hover:bg-accent-600",
    ring: "border-accent-200",
  },
  secondary: {
    icon: "bg-secondary-500 text-background-50",
    button: "bg-secondary-500 text-background-50 hover:bg-secondary-600",
    ring: "border-secondary-200",
  },
};

const noticeMap: Record<string, Notice> = {
  e1: {
    title: "网页直连入口",
    lines: [
      "当前推荐地址：51heilian01.top（10:24 检测为顺畅）。",
      "复制后粘贴到浏览器地址栏即可打开，无需安装任何程序。",
      "若提示无法访问，请回到「线路状态」区切换其他线路。",
    ],
    copyText: "51heilian01.top",
    copyLabel: "复制主站地址",
  },
  e2: {
    title: "App 下载安装方式",
    lines: [
      "安卓端：下载安装包后按提示完成安装，如提示来源限制请在设置中允许本次安装。",
      "iOS 端：按页面给出的方式添加到主屏幕后即可从桌面直接打开。",
      "安装完成后 App 会自动记忆可用入口，地址变动也能一键回到。",
    ],
    copyText: "51heilian-app.top/download",
    copyLabel: "复制下载地址",
  },
  e3: {
    title: "备用线路合集",
    lines: [
      "本站共维护 6 条线路，包含主站直达、备用入口与海外专线。",
      "建议把全部线路一次收藏，主站打不开时可逐条切换。",
      "延迟越低代表访问越顺畅，优先选择「顺畅」状态的线路。",
    ],
    copyText: "51heilian01.top / 51heilian02.top / hl-mobile.net",
    copyLabel: "复制全部线路",
  },
};

export default function EntrySection({ onOpenNotice }: EntrySectionProps) {
  return (
    <section id="entry" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        <SectionHeading
          eyebrow="ENTRY & DOWNLOAD"
          title="入口 · 下载 · 备用，三条路一次到位"
          desc="网页直连最省事，App 记忆入口最省心，备用线路合集最稳妥，按你的场景挑一条即可。"
          icon="ri-door-open-line"
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {entryChannels.map((channel) => {
            const accent = accentMap[channel.accent] ?? accentMap.primary;
            return (
              <div
                key={channel.id}
                className={`flex flex-col rounded-lg border bg-background-50 p-5 ${accent.ring}`}
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${accent.icon}`}>
                  <i className={`${channel.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-lg font-bold text-foreground-950">{channel.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{channel.desc}</p>
                <button
                  type="button"
                  onClick={() => onOpenNotice(noticeMap[channel.id])}
                  className={`mt-5 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-bold transition-colors ${accent.button}`}
                >
                  <i className="ri-arrow-right-circle-line text-base"></i>
                  {channel.action}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:mt-8 md:p-6">
          <div className="mb-5 flex items-center gap-2">
            <i className="ri-guide-line text-lg text-primary-600"></i>
            <h3 className="text-base font-bold text-foreground-950">三步找到当下可用入口</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            {accessSteps.map((step, index) => (
              <div key={step.id} className="relative flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 font-heading text-lg font-black text-secondary-800">
                  {step.step}
                </span>
                <div>
                  <h4 className="text-sm font-bold text-foreground-950">
                    <a href="#lines" className="hover:text-primary-600">
                      {step.title}
                    </a>
                  </h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                </div>
                {index < accessSteps.length - 1 ? (
                  <i className="ri-arrow-right-line absolute -right-3 top-3 hidden text-background-500 md:block"></i>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}